import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsComplete(true);
            onComplete?.();
          }, 800);
          return 100;
        }
        return prevProgress + Math.random() * 12;
      });
    }, 120);

    return () => clearInterval(timer);
  }, [onComplete]);

  // 3D Spider Web Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let nodes = [];
    let spider = null;
    let webLines = [];
    let webLineProgress = []; // Track animation progress for each line
    let buildProgress = 0; // Overall web building progress (0-1)
    let maxLinesToShow = 0;

    // Spider node - Define classes FIRST
    class Spider {
      constructor() {
        this.x = canvas.width / 2;
        this.y = canvas.height / 2;
        this.targetX = this.x;
        this.targetY = this.y;
        this.size = 12;
        this.speed = 0.04;
        this.angle = 0;
        this.legPhase = 0;
        this.currentNodeIndex = 0;
        this.webBuildingMode = true;
        this.silkTrail = []; // Trail of silk behind spider
      }

      update() {
        // Smooth movement towards target
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        const distance = Math.hypot(dx, dy);

        if (distance > 2) {
          this.x += dx * this.speed;
          this.y += dy * this.speed;
          this.angle = Math.atan2(dy, dx);
          this.legPhase += 0.15; // Slower, more realistic leg movement
        } else {
          // Slow down when close to target
          this.legPhase += 0.05;
        }

        // Intelligent path following along octagonal web
        if (distance < 15 && nodes.length > 0) {
          // Move along web edges - prefer connected nodes
          if (webLines.length > 0) {
            // Find a connected node from current position
            const connectedNodes = webLines
              .filter(([i1, i2]) => 
                (i1 === this.currentNodeIndex || i2 === this.currentNodeIndex) &&
                nodes[i1] && nodes[i2]
              )
              .map(([i1, i2]) => i1 === this.currentNodeIndex ? i2 : i1);
            
            if (connectedNodes.length > 0) {
              // Prefer nodes that create octagonal pattern (every 8th node or adjacent)
              const preferredNode = connectedNodes.find(idx => 
                Math.abs(idx - this.currentNodeIndex) <= 2 || 
                Math.abs(idx - this.currentNodeIndex) % 8 === 0
              ) || connectedNodes[Math.floor(Math.random() * connectedNodes.length)];
              
              this.currentNodeIndex = preferredNode;
            } else {
              // Fallback: move sequentially
              this.currentNodeIndex = (this.currentNodeIndex + 1) % nodes.length;
            }
          } else {
            this.currentNodeIndex = (this.currentNodeIndex + 1) % nodes.length;
          }
          
          const targetNode = nodes[this.currentNodeIndex];
          if (targetNode) {
            this.targetX = targetNode.x;
            this.targetY = targetNode.y;
          }
        }

        // Update silk trail
        this.silkTrail.push({ x: this.x, y: this.y, age: 0 });
        if (this.silkTrail.length > 15) {
          this.silkTrail.shift();
        }
        this.silkTrail.forEach(point => point.age++);
      }

      draw() {
        // Animated spider legs (8 legs with realistic movement)
        for (let i = 0; i < 8; i++) {
          const baseAngle = (Math.PI * 2 * i) / 8 + this.angle;
          const legPhaseOffset = i % 2 === 0 ? this.legPhase : this.legPhase + Math.PI;
          const legBend = Math.sin(legPhaseOffset) * 0.3;
          
          const legLength = this.size * 2.5;
          const midLength = legLength * 0.6;
          
          // First leg segment
          const midX = this.x + Math.cos(baseAngle + legBend) * midLength;
          const midY = this.y + Math.sin(baseAngle + legBend) * midLength;
          
          // Second leg segment
          const endX = midX + Math.cos(baseAngle - legBend * 0.5) * (legLength - midLength);
          const endY = midY + Math.sin(baseAngle - legBend * 0.5) * (legLength - midLength);
          
          ctx.strokeStyle = 'rgba(147, 51, 234, 0.8)';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(midX, midY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }

        // Spider body with enhanced gradient
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
        gradient.addColorStop(0, 'rgba(168, 85, 247, 1)');
        gradient.addColorStop(0.5, 'rgba(147, 51, 234, 0.9)');
        gradient.addColorStop(1, 'rgba(126, 34, 206, 0.5)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        // Spider glow effect
        ctx.shadowColor = 'rgba(168, 85, 247, 0.9)';
        ctx.shadowBlur = 25;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Spider head (smaller circle)
        ctx.fillStyle = 'rgba(147, 51, 234, 1)';
        ctx.beginPath();
        ctx.arc(
          this.x + Math.cos(this.angle) * this.size * 0.6,
          this.y + Math.sin(this.angle) * this.size * 0.6,
          this.size * 0.4,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
    }

    // Web node
    class WebNode {
      constructor(x, y, isAnchor = false) {
        this.x = x;
        this.y = y;
        this.originalX = x;
        this.originalY = y;
        this.vx = 0;
        this.vy = 0;
        this.isAnchor = isAnchor;
        this.size = isAnchor ? 4 : 3;
      }

      update() {
        if (this.isAnchor) return;

        // Apply physics
        const dx = this.originalX - this.x;
        const dy = this.originalY - this.y;
        
        this.vx += dx * 0.01;
        this.vy += dy * 0.01;
        
        this.vx *= 0.95;
        this.vy *= 0.95;
        
        this.x += this.vx;
        this.y += this.vy;

        // React to spider movement
        if (spider) {
          const distToSpider = Math.hypot(spider.x - this.x, spider.y - this.y);
          if (distToSpider < 100) {
            const angle = Math.atan2(this.y - spider.y, this.x - spider.x);
            const force = (100 - distToSpider) / 100;
            this.vx += Math.cos(angle) * force * 0.5;
            this.vy += Math.sin(angle) * force * 0.5;
          }
        }
      }

      draw() {
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
        gradient.addColorStop(0, this.isAnchor ? 'rgba(168, 85, 247, 1)' : 'rgba(192, 132, 252, 0.8)');
        gradient.addColorStop(1, 'rgba(147, 51, 234, 0.3)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize octagonal web structure
    function initializeWeb() {
      nodes = [];
      webLines = [];
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const rings = 12; // Even more rings for complexity
      const sides = 8; // Octagonal - 8 sides
      const maxRadius = Math.max(canvas.width, canvas.height) * 0.48;

      // Add center point
      nodes.push(new WebNode(centerX, centerY, false));

      // Create octagonal rings
      for (let ring = 1; ring <= rings; ring++) {
        const radius = (maxRadius * ring) / rings;
        
        // Create octagonal points (8 corners + midpoints for complexity)
        for (let side = 0; side < sides; side++) {
          const angle = (Math.PI * 2 * side) / sides - Math.PI / 2; // Start from top
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          const isAnchor = ring === rings;
          nodes.push(new WebNode(x, y, isAnchor));
          
          // Add midpoints between corners for extra complexity
          if (ring > 2 && ring % 2 === 0) {
            const nextAngle = (Math.PI * 2 * (side + 1)) / sides - Math.PI / 2;
            const midAngle = (angle + nextAngle) / 2;
            const midX = centerX + Math.cos(midAngle) * radius;
            const midY = centerY + Math.sin(midAngle) * radius;
            nodes.push(new WebNode(midX, midY, false));
          }
        }
      }

      // Create web connections
      webLines = [];
      
      // Connect center to first ring
      for (let i = 1; i <= sides; i++) {
        webLines.push([0, i]);
      }

      // Create octagonal perimeter connections and radial spokes
      let currentNodeIndex = 1;
      
      for (let ring = 1; ring <= rings; ring++) {
        const nodesInRing = (ring > 2 && ring % 2 === 0) ? sides * 2 : sides;
        const ringStartIndex = currentNodeIndex;
        
        // Octagonal perimeter for this ring
        for (let i = 0; i < nodesInRing; i++) {
          const current = ringStartIndex + i;
          const next = ringStartIndex + ((i + 1) % nodesInRing);
          webLines.push([current, next]);
        }
        
        // Radial connections to next ring
        if (ring < rings) {
          const nextRingStart = ringStartIndex + nodesInRing;
          const nextRingNodes = (ring + 1 > 2 && (ring + 1) % 2 === 0) ? sides * 2 : sides;
          
          for (let i = 0; i < nodesInRing; i++) {
            const current = ringStartIndex + i;
            // Connect to corresponding node in next ring
            const correspondingNext = nextRingStart + Math.floor((i * nextRingNodes) / nodesInRing);
            webLines.push([current, correspondingNext]);
            
            // Add diagonal support lines for strength
            if (i % 2 === 0 && nextRingNodes > sides) {
              const diagonalNext = nextRingStart + ((Math.floor((i * nextRingNodes) / nodesInRing) + 1) % nextRingNodes);
              webLines.push([current, diagonalNext]);
            }
          }
        }
        
        currentNodeIndex += nodesInRing;
      }

      // Add spiral connecting lines for extra complexity
      currentNodeIndex = 1 + sides;
      for (let ring = 2; ring <= rings - 1; ring++) {
        const nodesInRing = (ring > 2 && ring % 2 === 0) ? sides * 2 : sides;
        for (let i = 0; i < Math.min(nodesInRing, sides); i++) {
          const current = currentNodeIndex + i;
          const spiralTarget = currentNodeIndex + ((i + 2) % nodesInRing);
          if (Math.abs(current - spiralTarget) > 1) {
            webLines.push([current, spiralTarget]);
          }
        }
        currentNodeIndex += nodesInRing;
      }

      // Add skip-ring connections for reinforcement (every 2nd ring)
      currentNodeIndex = 1;
      for (let ring = 1; ring <= rings - 2; ring++) {
        const nodesInRing = (ring > 2 && ring % 2 === 0) ? sides * 2 : sides;
        const ringStartIndex = currentNodeIndex;
        
        // Skip to next-next ring
        let nextRingIndex = currentNodeIndex + nodesInRing;
        const nextRingNodes = (ring + 1 > 2 && (ring + 1) % 2 === 0) ? sides * 2 : sides;
        nextRingIndex += nextRingNodes; // Skip one ring
        
        if (ring % 2 === 0) {
          for (let i = 0; i < sides; i++) {
            const current = ringStartIndex + i;
            const skipTarget = nextRingIndex + i;
            if (skipTarget < nodes.length) {
              webLines.push([current, skipTarget]);
            }
          }
        }
        currentNodeIndex += nodesInRing;
      }

      // Add decorative cross-weave pattern in outer rings
      currentNodeIndex = 1 + sides;
      for (let ring = 2; ring <= rings; ring++) {
        const nodesInRing = (ring > 2 && ring % 2 === 0) ? sides * 2 : sides;
        const ringStartIndex = currentNodeIndex;
        
        if (ring > 6) { // Only outer rings
          for (let i = 0; i < nodesInRing; i += 2) {
            const current = ringStartIndex + i;
            const oppositeTarget = ringStartIndex + ((i + nodesInRing / 2) % nodesInRing);
            if (Math.abs(current - oppositeTarget) > 2) {
              webLines.push([current, oppositeTarget]);
            }
          }
        }
        currentNodeIndex += nodesInRing;
      }

      spider = new Spider();
      
      // Initialize line progress array for animation
      webLineProgress = webLines.map(() => 0);
      buildProgress = 0;
      maxLinesToShow = 0;
    }

    // Setup canvas and event listeners - Call AFTER class definitions
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initializeWeb();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Draw animated web line with gradient and building effect
    function drawWebLine(node1, node2, progress = 1, isPrimary = false) {
      const distance = Math.hypot(node2.x - node1.x, node2.y - node1.y);
      const maxDistance = 200;
      const opacity = Math.max(0.1, 1 - distance / maxDistance);

      // Animate line drawing from node1 to node2
      const endX = node1.x + (node2.x - node1.x) * progress;
      const endY = node1.y + (node2.y - node1.y) * progress;

      const gradient = ctx.createLinearGradient(node1.x, node1.y, endX, endY);
      const baseOpacity = opacity * progress;
      
      if (isPrimary) {
        // Primary structural lines (brighter)
        gradient.addColorStop(0, `rgba(168, 85, 247, ${baseOpacity * 0.9})`);
        gradient.addColorStop(0.5, `rgba(192, 132, 252, ${baseOpacity})`);
        gradient.addColorStop(1, `rgba(147, 51, 234, ${baseOpacity * 0.8})`);
        ctx.lineWidth = 2;
      } else {
        // Secondary support lines
        gradient.addColorStop(0, `rgba(147, 51, 234, ${baseOpacity * 0.6})`);
        gradient.addColorStop(0.5, `rgba(168, 85, 247, ${baseOpacity * 0.7})`);
        gradient.addColorStop(1, `rgba(192, 132, 252, ${baseOpacity * 0.5})`);
        ctx.lineWidth = 1.5;
      }

      ctx.strokeStyle = gradient;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(node1.x, node1.y);
      ctx.lineTo(endX, endY);
      ctx.stroke();

      // Add shimmer effect at growing tip
      if (progress < 1) {
        ctx.shadowColor = 'rgba(192, 132, 252, 0.8)';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(endX, endY, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(192, 132, 252, 0.9)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Add shimmer line
      if (progress > 0.3) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity * 0.3})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }

    // Animation loop
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Progressive web building animation
      buildProgress += 0.008; // Gradual build speed
      if (buildProgress > 1) buildProgress = 1;
      
      maxLinesToShow = Math.floor(webLines.length * buildProgress);

      // Update node physics
      nodes.forEach(node => node.update());
      
      // Draw web lines progressively with animation
      for (let i = 0; i < maxLinesToShow; i++) {
        const [i1, i2] = webLines[i];
        if (nodes[i1] && nodes[i2]) {
          // Update line progress
          if (webLineProgress[i] < 1) {
            webLineProgress[i] += 0.05; // Line drawing speed
            if (webLineProgress[i] > 1) webLineProgress[i] = 1;
          }
          
          // Determine if primary structural line (center spokes and first ring)
          const isPrimary = i < 20 || (i1 === 0 || i2 === 0);
          
          drawWebLine(nodes[i1], nodes[i2], webLineProgress[i], isPrimary);
        }
      }

      // Draw node highlights (after some web is built)
      if (buildProgress > 0.2) {
        nodes.forEach(node => node.draw());
      }

      // Update and draw spider
      if (spider) {
        spider.update();
        
        // Draw silk trail behind spider
        if (spider.silkTrail.length > 1) {
          for (let i = 0; i < spider.silkTrail.length - 1; i++) {
            const point = spider.silkTrail[i];
            const nextPoint = spider.silkTrail[i + 1];
            const trailOpacity = (1 - point.age / 15) * 0.5;
            
            ctx.strokeStyle = `rgba(192, 132, 252, ${trailOpacity})`;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
            ctx.lineTo(nextPoint.x, nextPoint.y);
            ctx.stroke();
          }
        }
        
        // Draw silk line from spider to current target node
        if (nodes.length > 0 && buildProgress > 0.1) {
          const targetNode = nodes[spider.currentNodeIndex];
          if (targetNode) {
            const gradient = ctx.createLinearGradient(
              spider.x, spider.y, targetNode.x, targetNode.y
            );
            gradient.addColorStop(0, 'rgba(192, 132, 252, 0.7)');
            gradient.addColorStop(1, 'rgba(147, 51, 234, 0.3)');
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2.5;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(spider.x, spider.y);
            ctx.lineTo(targetNode.x, targetNode.y);
            ctx.stroke();
            
            // Glowing effect at connection point
            ctx.shadowColor = 'rgba(192, 132, 252, 0.8)';
            ctx.shadowBlur = 10;
            ctx.beginPath();
            ctx.arc(targetNode.x, targetNode.y, 4, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(192, 132, 252, 0.6)';
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        }

        spider.draw();
      }

      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 1 },
    exit: {
      opacity: 0,
      scale: 1.05,
      transition: {
        duration: 1,
        ease: "easeInOut"
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const progressVariants = {
    hidden: { width: 0 },
    visible: {
      width: `${progress}%`,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-br from-black via-black to-purple-950 overflow-hidden"
        >
          {/* 3D Spider Web Canvas */}
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.95 }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/60 to-black pointer-events-none" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;